import React from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import PageHeader from '../components/PageHeader';
import CompanyLogos from '../components/CompanyLogos';
import { services } from '../mock';
import {
  ArrowUpRight, Check, Building2, Wallet, Shield,
  Calendar, PlayCircle, Mail,
} from 'lucide-react';
import { SectionHeader } from '../components/Services';

const steps = [
  { title: 'EA Tax Training', desc: 'Complete the Enrolled Agent pathway with live classes, recordings, question bank, doubt solving, test batches, and exam registration support.' },
  { title: 'US Business Formation', desc: 'Set up your US company, apply for EIN, arrange liability insurance, and prepare the operating structure for your tax practice.' },
  { title: 'India Business Formation', desc: 'Form the Indian business side with GST, LUT under GST, and inward remittance support for US-to-India transactions.' },
  { title: 'Sales Tax & Payroll Tax', desc: 'Learn the business-side workflow for sales tax, payroll tax, and tax-manager-guided operating procedures.' },
  { title: 'Accounting Operations', desc: 'Practice bank reconciliation, invoicing, bill payments, month-end reports, payroll entries, P&L, balance sheet, cash flow, AP, AR, and GL reports.' },
  { title: 'Cybersecurity Setup', desc: 'Protect the business with email security, mobile security, router encryption, VPN/static IP, backups, firewall setup, and audit support.' },
];

const businessTraining = [
  {
    title: 'Business Operations',
    points: ['Standard operating procedure training', 'CRM and tax software procedure', 'Professional email communication', 'Customer management and retention training'],
  },
  {
    title: 'Accounting Workflow',
    points: ['Data entry and bank reconciliation', 'Invoices, bills, AP and AR reports', 'Month-end reports, P&L, balance sheet, cash flow', 'Trial balance, general ledger, and working capital analysis'],
  },
  {
    title: 'Digital Tools',
    points: ['Official business email ID', 'Google Workspace or Microsoft 365 setup', 'Website creation', 'US phone number with SMS'],
  },
];

const timeline = [
  { week: 'Month 1-6', title: 'EA course training', points: ['Daily live classes from Monday to Friday', 'Recorded lectures and backup classes', 'Question bank and doubt solver classes', 'Test batches, exam registration assistance, and study materials'] },
  { week: 'Month 7-10', title: 'Business training', points: ['Sales tax and payroll tax training', 'Business SOP training', 'CRM, tax software, and mail procedure', 'Business setup in India and the US'] },
  { week: 'Month 10-24', title: 'Business support', points: ['Cybersecurity maintenance', 'Business support and maintenance', 'Service workflow and consultation support', 'Ongoing operational guidance'] },
];

const support = [
  { icon: Shield, title: 'Cybersecurity', desc: 'Email security, mobile security, router encryption, ransomware prevention, data backup, VPN/static IP, and firewall setup.' },
  { icon: Building2, title: 'US & India setup', desc: 'US company formation, EIN support, liability insurance, Indian company formation, GST, LUT, and remittance-purpose code support.' },
  { icon: Wallet, title: 'Accounting systems', desc: 'Bookkeeping workflow, payroll entries, tax adjustment entries, reports, reconciliation, and business finance procedure training.' },
  { icon: Mail, title: 'Business communication', desc: 'Clear subject lines, professional greetings, concise writing, organised structure, actionable requests, and follow-up standards.' },
];

const faqs = [
  { q: 'What is the duration of the Business Setup program?', a: 'The full program duration is 24 months, including EA training, business training, business setup, cybersecurity setup, and continued support.' },
  { q: 'What is included in the setup price?', a: 'The PDF lists the setup charges as ₹5,50,000. The price includes the listed training, setup, cybersecurity, tools, and business-support services offered in the program.' },
  { q: 'What documents are required?', a: '10+2 certificate, degree completed certificate, passport, Aadhaar card, and PAN card are required for verification.' },
  { q: 'Is an entrance exam required?', a: 'No entrance exam is required. Eligibility is checked by speaking with the Eduvantage team.' },
  { q: 'What is the teaching medium?', a: 'The medium of teaching is English.' },
];

const bigNumbers = [
  { n: '24', l: 'months program duration' },
  { n: '₹5.5L', l: 'setup charges' },
  { n: '10', l: 'months business training and setup' },
  { n: '5', l: 'required documents' },
];

const BusinessSetup = () => {
  return (
    <div className="font-body bg-paper text-[#1c1c1c]">
      <Navbar />
      <PageHeader
        eyebrow="Business Setup Course Code EA02"
        title={<>U.S. tax firm <em className="italic">business setup</em></>}
        description="A 24-month program covering EA training, US and India business formation, accounting operations, cybersecurity, SOP training, and business maintenance."
        breadcrumb="Business Setup"
      />

      {/* Steps */}
      <section className="py-16 md:py-20">
        <div className="max-w-6xl mx-auto px-6">
          <SectionHeader
            eyebrow="Step-by-step Process"
            title={<>Training, setup, and <em className="italic">business systems</em></>}
            description="The EA02 program combines tax training with business formation, operations training, cybersecurity, and ongoing maintenance."
          />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
            {steps.map((s, i) => (
              <div key={i} className="card-soft rounded-2xl p-6 md:p-7">
                <div className="font-serif-display text-[#143324] text-4xl italic mb-3">{String(i + 1).padStart(2, '0')}</div>
                <h3 className="font-body font-semibold text-[#143324] text-lg mb-2">{s.title}</h3>
                <p className="text-[13px] text-[#3d3d3d] leading-relaxed font-body">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="py-16 md:py-20 bg-paper-soft">
        <div className="max-w-4xl mx-auto px-6">
          <div className="card-soft rounded-2xl p-8 md:p-12 relative overflow-hidden">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
              <div>
                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[#143324]/15 bg-white/60 text-[11px] tracking-[0.18em] uppercase text-[#143324] font-body mb-4">
                  Complete Package
                </div>
                <h3 className="font-serif-display text-[#143324] text-4xl md:text-5xl mb-3">
                  Setup charges <em className="italic">₹5,50,000</em>
                </h3>
                <p className="text-[#3d3d3d] text-[14px] leading-relaxed font-body">
                  Price includes the costs and services offered in the EA02 Business Setup program.
                </p>
              </div>
              <div>
                <ul className="space-y-3 mb-6">
                  {['EA course training','US company formation and EIN','India company formation with GST and LUT','Sales tax and payroll tax training','Cybersecurity installation','Business support and maintenance'].map((f, i) => (
                    <li key={i} className="flex items-center gap-3 text-[14px] font-body text-[#1c1c1c]">
                      <Check size={16} className="text-[#143324]" strokeWidth={2.5} /> {f}
                    </li>
                  ))}
                </ul>
                <Link to="/contact" className="inline-flex items-center gap-2 btn-primary px-6 py-3 rounded-lg text-[14px] font-body">
                  Talk to an Expert <ArrowUpRight size={15} />
                </Link>
              </div>
            </div>
            <img src={services[2].images[0]} alt="setup" className="absolute -bottom-16 -right-16 w-64 h-64 object-cover rounded-2xl opacity-30 pointer-events-none hidden md:block" />
          </div>
        </div>
      </section>

      {/* Business Training */}
      <section className="py-16 md:py-24">
        <div className="max-w-6xl mx-auto px-6">
          <SectionHeader
            eyebrow="Everything About the Business"
            title={<>Operations taught with <em className="italic">tax-manager guidance</em></>}
            description="The program covers the operating systems needed to run the business properly, from accounting workflow to communication and tools."
          />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
            {businessTraining.map((s, i) => (
              <div key={i} className="card-soft rounded-2xl p-6 md:p-7">
                <h3 className="font-serif-display text-[#143324] text-3xl mb-4">{s.title}</h3>
                <ul className="space-y-2 text-[13px] text-[#3d3d3d] font-body">
                  {s.points.map((p, j) => (
                    <li key={j} className="flex items-start gap-2">
                      <Check size={13} className="mt-1 text-[#143324]" /> {p}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 8-week Timeline */}
      <section className="py-16 md:py-24 bg-paper-soft relative">
        <div className="squiggle-bg" />
        <div className="max-w-6xl mx-auto px-6 relative">
          <SectionHeader
            eyebrow="Timeline"
            title={<>A 24-month <em className="italic">training and support</em> path</>}
            description="The PDF timeline is structured across EA training, business training, setup, cybersecurity, and continued support."
          />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
            {timeline.map((t, i) => (
              <div key={i} className="card-soft rounded-2xl p-6 md:p-7 relative">
                <div className="absolute -top-3 left-6 inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#143324] text-[#f5f1e6] text-[11px] tracking-wider uppercase font-body">
                  <Calendar size={11} /> {t.week}
                </div>
                <h3 className="font-body font-semibold text-[#143324] text-lg mt-3 mb-3">{t.title}</h3>
                <ul className="space-y-2 text-[13px] text-[#3d3d3d] font-body">
                  {t.points.map((p, j) => (
                    <li key={j} className="flex items-start gap-2">
                      <span className="mt-1 w-1.5 h-1.5 rounded-full bg-[#143324] shrink-0" />
                      {p}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Ongoing Support */}
      <section className="py-16 md:py-24">
        <div className="max-w-6xl mx-auto px-6">
          <SectionHeader
            eyebrow="Business Support and Maintenance"
            title={<>Systems, security, and <em className="italic">operating discipline</em></>}
            description="Support focuses on setup, maintenance, cybersecurity, accounting workflow, and professional communication."
          />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
            {support.map((s, i) => {
              const Icon = s.icon;
              return (
                <div key={i} className="card-soft rounded-2xl p-6 md:p-7">
                  <div className="w-11 h-11 rounded-xl bg-[#143324] text-[#f5f1e6] flex items-center justify-center mb-4">
                    <Icon size={18} strokeWidth={1.7} />
                  </div>
                  <h3 className="font-body font-semibold text-[#143324] text-[15px] mb-2">{s.title}</h3>
                  <p className="text-[13px] text-[#3d3d3d] leading-relaxed font-body">{s.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Big Numbers */}
      <section className="py-16 md:py-20 bg-paper-soft">
        <div className="max-w-6xl mx-auto px-6">
          <div className="rounded-3xl p-8 md:p-14 bg-[#f5f1e6] text-[#143324] border border-[#143324]/10 shadow-sm">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
              {bigNumbers.map((b, i) => (
                <div key={i} className="min-w-0">
                  <div className="font-serif-display text-[#143324] text-4xl md:text-6xl leading-none mb-2">{b.n}</div>
                  <p className="text-[13px] text-[#2f3a32] font-body leading-snug">{b.l}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Documents */}
      <section className="py-16 md:py-24">
        <div className="max-w-5xl mx-auto px-6">
          <SectionHeader
            eyebrow="Eligibility and Documents"
            title={<>What you need before <em className="italic">onboarding</em></>}
            description="Eligibility is checked by speaking with the team. No entrance exam is required."
          />
          <div className="card-soft rounded-2xl p-6 md:p-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6">
              {['10+2 certificate','Degree completed certificate','Passport','Aadhaar card','PAN card','Speak to the team for eligibility check'].map((item, i) => (
                <div key={i} className="flex items-center gap-3 text-[14px] text-[#1c1c1c] font-body">
                  <Check size={16} className="text-[#143324]" strokeWidth={2.5} />
                  {item}
                </div>
              ))}
            </div>
            <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
              {[
                ['Medium', 'English'],
                ['Entrance Exam', 'Not required'],
                ['Currency', 'Indian Rupees'],
              ].map(([label, value]) => (
                <div key={label} className="rounded-xl bg-[#f5f1e6] border border-[#143324]/10 p-4">
                  <p className="text-[11px] uppercase tracking-wider text-[#3d3d3d] font-body mb-1">{label}</p>
                  <p className="text-[#143324] font-body font-semibold">{value}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Onboarding */}
      <section className="py-16 md:py-24 bg-paper-soft">
        <div className="max-w-6xl mx-auto px-6">
          <SectionHeader
            eyebrow="Onboarding Procedure"
            title={<>From trial class to <em className="italic">orientation</em></>}
            description="A clear onboarding process before the program begins."
          />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
            {[
              'Attend the trial class.',
              'Attend business setup counselling and clear doubts on US taxation.',
              'Verify Aadhaar, passport, degree certificate, PAN card, and 10+2 marksheet.',
              'Visit the office, sign the engagement letter, complete payment steps, receive login credentials, and attend orientation.',
            ].map((item, i) => (
              <div key={i} className="card-soft rounded-2xl p-6">
                <div className="font-serif-display text-[#143324] text-4xl italic mb-3">{String(i + 1).padStart(2, '0')}</div>
                <p className="text-[13.5px] text-[#3d3d3d] leading-relaxed font-body">{item}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 md:py-24 bg-paper-soft">
        <div className="max-w-4xl mx-auto px-6">
          <SectionHeader
            eyebrow="Frequently Asked"
            title={<>The <em className="italic">legal & compliance</em> questions</>}
            description="Straight-up answers. No mystery clauses."
          />
          <div className="space-y-3">
            {faqs.map((f, i) => (
              <details key={i} className="card-soft rounded-2xl px-6 py-5 group" data-testid={`bs-faq-${i}`}>
                <summary className="cursor-pointer font-body font-semibold text-[#143324] text-[15px] flex items-center justify-between list-none">
                  <span>{f.q}</span>
                  <span className="ml-3 w-6 h-6 rounded-full bg-[#f5f1e6] border border-[#143324]/15 flex items-center justify-center text-[#143324] group-open:rotate-45 transition-transform">+</span>
                </summary>
                <p className="mt-3 text-[13.5px] text-[#3d3d3d] leading-relaxed font-body">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 md:py-24">
        <div className="max-w-5xl mx-auto px-6">
          <div className="rounded-3xl p-8 md:p-14 bg-[#f5f1e6] text-[#143324] border border-[#143324]/10 shadow-sm relative overflow-hidden">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
              <div>
                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[#143324]/15 bg-white/60 text-[11px] tracking-[0.2em] uppercase text-[#143324] font-body mb-4">
                  <PlayCircle size={12} /> Trial class and counselling
                </div>
                <h3 className="font-serif-display text-[#143324] text-4xl md:text-5xl leading-tight mb-4">
                  Start with the <em className="italic">EA02 program</em>.
                </h3>
                <p className="text-[#2f3a32] font-body leading-relaxed mb-2 max-w-md">
                  Attend a trial class, speak with the team, verify eligibility, and understand the full business setup pathway before onboarding.
                </p>
              </div>
              <div className="flex flex-col sm:flex-row md:flex-col gap-3">
                <Link to="/contact" className="btn-primary inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full text-[14px] font-body">
                  Book counselling <ArrowUpRight size={15} />
                </Link>
                <a href="https://wa.me/917010492161" target="_blank" rel="noreferrer" className="btn-outline inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full text-[14px] font-body">
                  WhatsApp us
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      <CompanyLogos />
      <Footer />
    </div>
  );
};

export default BusinessSetup;
